import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "id", "labelWrapperStyles", "position", "text", "error", "onChange"];
import { Children, cloneElement } from 'react';
import { MediaObject } from '../MediaObject/MediaObject';
import { TruncateString } from '../TruncateString/TruncateString';
import { Consumer as FieldsetContextConsumer } from '../../context/FieldsetContext';
import { warnIfFragment } from '../../utils/warnIfFragment/warnIfFragment';
import { MediaLeft } from '../MediaLeft/MediaLeft';
import { MediaRight } from '../MediaRight/MediaRight';
import { InputStaticLabelProvider } from './InputStaticLabelProvider';
import styled, { useTheme } from 'foundations-theming/utils';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { useIsDisplayOptimized } from '../../context/DisplayOptimizedContext';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledInputStaticLabel = styled.label.withConfig({
  displayName: "InputStaticLabel__StyledInputStaticLabel",
  componentId: "sc-183551d-0"
})(({
  $cssStyles
}) => {
  return Object.assign({
    cursor: 'default'
  }, $cssStyles);
});
const getLeft = styles => ({
  children
}) => {
  return /*#__PURE__*/_jsx(MediaLeft, Object.assign({}, styles, {
    children: children
  }));
};
const getRight = styles => ({
  children
}) => {
  return /*#__PURE__*/_jsx(MediaRight, Object.assign({}, styles, {
    children: children
  }));
};
export const InputStaticLabel = _ref => {
  let {
      children,
      id,
      labelWrapperStyles,
      position = 'start',
      text,
      error,
      onChange
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = useTheme();
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const isDisplayOptimized = useIsDisplayOptimized();
  const renderedLabel = /*#__PURE__*/_jsx(TruncateString, {
    useFlex: true,
    children: text
  });
  const cloneProps = {
    id: children.props.id || id
  };

  // avoid "unknown prop" warning when `error` is undefined
  if (error !== undefined) {
    cloneProps.error = error;
  }
  const onlyChild = Children.only(children);
  warnIfFragment(onlyChild, 'InputStaticLabel');
  return /*#__PURE__*/_jsx(FieldsetContextConsumer, {
    children: ({
      size: fieldSize
    }) => {
      return /*#__PURE__*/_jsx(InputStaticLabelProvider, {
        truncateStringStyleOverrides: {
          paddingBlock: isDisplayOptimized ? theme.space['50'] : fieldSize === 'small' ? theme.space['100'] : theme.space['200'],
          paddingInline: theme.space['0'],
          fontSize: fieldSize === 'small' || isDisplayOptimized ? theme.typography['body-100'].fontSize : theme.typography['body-200'].fontSize,
          userSelect: 'none',
          blockSize: isDisplayOptimized ? '30px' : fieldSize === 'small' ? '32px' : '40px'
        },
        children: /*#__PURE__*/_jsx(StyledInputStaticLabel, Object.assign({}, domProps, {
          onChange: onChange,
          $cssStyles: cssStyles,
          children: /*#__PURE__*/_jsx(MediaObject, {
            spacing: "extra-small",
            itemLeft: position === 'start' ? renderedLabel : undefined,
            MediaLeft: getLeft(Object.assign({}, labelWrapperStyles, {
              maxInlineSize: '50%'
            })),
            itemRight: position === 'end' ? renderedLabel : undefined,
            MediaRight: getRight(Object.assign({}, labelWrapperStyles, {
              maxInlineSize: '50%'
            })),
            children: /*#__PURE__*/cloneElement(onlyChild, cloneProps)
          })
        }))
      });
    }
  });
};
InputStaticLabel.displayName = 'InputStaticLabel';